import { FunctionComponent, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../configuration/setup/hooks';
import { useGetTransportAssignmentIdsMutation } from '../../api/transportOrderApi';
import { FilterMaterializationExceededErrorResponseCodec } from '../../api/types/compoundManagement/errorCodes';
import { handleQueryError } from '../../notifications/ErrorNotification';
import {
    getFilters,
    getTransportAssignmentSearchMode,
    getTransportAssignmentSearchTerm,
    transportAssignmentsSlice,
} from '../../reducers/transportAssignmentsSlice';
import { BulkSelectionDropdown } from '../../sharedComponents/table/BulkSelectionDropdown';
import { mapFilterToQueryParam, mapSearchValueToQueryParam } from '../filter.types';

export const FIXED_TRANSPORT_ASSIGNMENT_CACHE_KEY = 'shared-get-transport-assignment-ids';
export const TRANSPORT_ASSIGNMENT_MAX_SELECTION_SIZE = 50;

export const TransportAssignmentBulkSelectionDropdown: FunctionComponent = () => {
    const dispatch = useAppDispatch();
    const { getIdsToUpdateSelection, hasTooManyFilterResultsError, resetTooManyFilterResultsError } =
        useGetTransportAssignmentIds();

    const handleAddFilterResultToSelection = async () => {
        const ids = await getIdsToUpdateSelection();
        if (ids !== undefined) {
            dispatch(transportAssignmentsSlice.actions.addToBulkSelection(ids));
        }
    };

    const handleRemoveFilterResultFromSelection = async () => {
        const ids = await getIdsToUpdateSelection();
        if (ids !== undefined) {
            dispatch(transportAssignmentsSlice.actions.removeFromBulkSelection(ids));
        }
    };

    const handleClearSelection = () => {
        dispatch(transportAssignmentsSlice.actions.clearBulkSelection());
    };

    return (
        <BulkSelectionDropdown
            addFilterResultToSelection={handleAddFilterResultToSelection}
            removeFilterResultFromSelection={handleRemoveFilterResultFromSelection}
            clearSelection={handleClearSelection}
            hasTooManyFilterResultsError={hasTooManyFilterResultsError}
            maxSelectionSize={TRANSPORT_ASSIGNMENT_MAX_SELECTION_SIZE}
            resetTooManyFilterResultsError={resetTooManyFilterResultsError}
        />
    );
};

const useGetTransportAssignmentIds = () => {
    const filters = useAppSelector(getFilters);
    const searchMode = useAppSelector(getTransportAssignmentSearchMode);
    const searchValue = useAppSelector(getTransportAssignmentSearchTerm);
    const [getTransportAssignmentIds] = useGetTransportAssignmentIdsMutation({
        fixedCacheKey: FIXED_TRANSPORT_ASSIGNMENT_CACHE_KEY,
    });
    const [hasTooManyFilterResultsError, setHasTooManyFilterResultsError] = useState(false);

    const getIdsToUpdateSelection = async (): Promise<string[] | undefined> => {
        try {
            return await getTransportAssignmentIds({
                ...mapFilterToQueryParam(filters),
                ...mapSearchValueToQueryParam(searchMode, searchValue),
            }).unwrap();
        } catch (error) {
            if (FilterMaterializationExceededErrorResponseCodec.is(error)) {
                setHasTooManyFilterResultsError(true);
            } else {
                handleQueryError(error);
            }
        }
    };

    const resetTooManyFilterResultsError = () => setHasTooManyFilterResultsError(false);

    return {
        getIdsToUpdateSelection,
        hasTooManyFilterResultsError,
        resetTooManyFilterResultsError,
    };
};
